import { useState, type FC, type ReactNode } from "react";
import { createContext, useCallback, useMemo } from "react";
import { matchPath, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { AlertColor } from "@mui/material";

import NewDashboardDialog from "../components/dialogs/NewDashboardDialog";
import EditDashboardDialog from "../components/dialogs/EditDashboardDialog";
import NewAlertDialog from "../components/dialogs/NewAlertDialog";
import EditAlertDialog from "../components/dialogs/EditAlertDialog";
import NewAlertSchemaDialog from "../components/dialogs/NewAlertSchemaDialog";
import EditAlertSchemaDialog from "../components/dialogs/EditAlertSchemaDialog";
import NewAlertEventDialog from "../components/dialogs/NewAlertEventDialog";
import NewServiceDialog from "../components/dialogs/NewServiceDialog";
import NewKpiDialog from "../components/dialogs/NewKpiDialog";
import NewServiceTemplateDialog from "../components/dialogs/NewServiceTemplateDialog";
import EditKpiDialog from "../components/dialogs/EditKpiDialog";
import EditServiceTemplateDialog from "../components/dialogs/EditServiceTemplateDialog";
import { useToast } from "../hooks/useToast";
import NewIntegrationDialog from "../components/dialogs/NewIntegrationDialog";
import EditIntegrationDialog from "../components/dialogs/EditIntegrationDialog";
import SetupIntegrationDialog from "../components/dialogs/SetupIntegrationDialog";
import EditServiceDialog from "../components/dialogs/EditServiceDialog";
import ConfirmDeleteDialog, {
  DeleteEntity,
} from "../components/dialogs/ConfirmDeleteDialog";
import ImportAlertsDialog from "../components/dialogs/ImportAlertsDialog";
import ManageDependenciesDialog from "../components/dialogs/ManageDependenciesDialog";
import ManageKpiAlertMappingsDialog from "../components/dialogs/ManageKpiAlertMappingsDialog";
import InviteUserDialog from "../components/dialogs/InviteUserDialog";
import EditUserDialog from "../components/dialogs/EditUserDialog";

type ShowNewDashboardDialog = { dialog: "new-dashboard" };
type ShowEditDashboardDialog = {
  dialog: "edit-dashboard";
  dashboardId: string;
};
type ShowNewAlertDialog = { dialog: "new-alert" };
type ShowEditAlertDialog = { dialog: "edit-alert"; alertId: string };
type ShowSetupIntegration = {
  dialog: "setup-integration";
  integrationId: string;
};
type ShowNewAlertProviderDialog = { dialog: "new-alert-schema" };
type ShowNewKpiDialog = { dialog: "new-kpi" };
type ShowNewServiceTemplateDialog = { dialog: "new-service-template" };
type ShowEditKpiDialog = { dialog: "edit-kpi"; kpiId: string };
type ShowEditServiceTemplateDialog = {
  dialog: "edit-service-template";
  serviceTemplateId: string;
};
type ShowEditAlertProviderDialog = {
  dialog: "edit-alert-schema";
  alertSchemaId: string;
};
type ShowNewAlertEventDialog = { dialog: "new-alert-event"; alertId: string };
type ShowNewSystemDialog = { dialog: "new-business-service"; dashboardId: string };
type ShowEditSystemDialog = {
  dialog: "edit-service";
  dashboardId: string;
  serviceId: string;
};
type ShowNewIntegrationDialog = { dialog: "new-integration" };
type ShowEditIntegrationDialog = {
  dialog: "edit-integration";
  integrationId: string;
};
type ShowConfirmDeleteDialog = {
  dialog: "confirm-delete";
  entity: DeleteEntity;
  ids: string;
  serviceMapId?: string;
};
type ShowImportAlertDialog = { dialog: "import-alerts"; integrationId?: string };
type ShowManageDependenciesDialog = {
  dialog: "manage-dependencies";
  serviceMapId: string;
  serviceId: string;
};
type ShowManageKpiAlertMappingsDialog = {
  dialog: "manage-kpi-alert-mappings";
  serviceMapId: string;
  serviceId: string;
};
type ShowInviteUserDialog = { dialog: "invite-user" };
type ShowEditUserDialog = { dialog: "edit-user"; userId: string };

export type DialogState =
  | ShowNewDashboardDialog
  | ShowEditDashboardDialog
  | ShowNewIntegrationDialog
  | ShowEditIntegrationDialog
  | ShowNewAlertDialog
  | ShowEditAlertDialog
  | ShowSetupIntegration
  | ShowNewAlertProviderDialog
  | ShowNewKpiDialog
  | ShowNewServiceTemplateDialog
  | ShowEditKpiDialog
  | ShowEditServiceTemplateDialog
  | ShowEditAlertProviderDialog
  | ShowNewAlertEventDialog
  | ShowNewSystemDialog
  | ShowEditSystemDialog
  | ShowImportAlertDialog
  | ShowConfirmDeleteDialog
  | ShowManageDependenciesDialog
  | ShowManageKpiAlertMappingsDialog
  | ShowInviteUserDialog
  | ShowEditUserDialog;

type ShowFunc = (
  dialog: DialogState,
  options?: { transient?: boolean },
) => void;

export type DialogContextValue = {
  active: DialogState | null;
  show: ShowFunc;
  close: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  previous: () => void;
  next: () => void;
};

const DialogContext = createContext<DialogContextValue>({
  active: null,
  show: () => undefined,
  close: () => undefined,
  hasPrevious: false,
  hasNext: false,
  previous: () => undefined,
  next: () => undefined,
});

export type CloseDialogHandler = (toast?: {
  message: string;
  severity: AlertColor;
}) => void;

const getDashboardIdFromPath = (pathname: string): string | null => {
  const matched = matchPath(
    { path: "/dashboards/:dashboardId/services", end: false },
    pathname,
  );
  return matched?.params.dashboardId ?? null;
};

const getServiceIdFromPath = (pathname: string): string | null => {
  const matched = matchPath(
    { path: "/dashboards/:dashboardId/services/:serviceId", end: true },
    pathname,
  );
  return matched?.params.serviceId ?? null;
};

const parseDialog = (sp: URLSearchParams, pathname: string): DialogState | null => {
  const dialog = sp.get("dialog");
  if (!dialog) {return null;}

  switch (dialog) {
    case "new-dashboard":
    case "new-alert":
    case "new-integration":
    case "new-alert-schema":
    case "new-kpi":
    case "new-service-template":
    case "invite-user":
      return { dialog } as DialogState;
    case "edit-user": {
      const userId = sp.get("userId") ?? sp.get("userEmail");
      return userId ? { dialog, userId } : null;
    }
    case "edit-kpi": {
      const kpiId = sp.get("kpiId");
      return kpiId ? { dialog, kpiId } : null;
    }
    case "edit-service-template": {
      const serviceTemplateId = sp.get("serviceTemplateId");
      return serviceTemplateId ? { dialog, serviceTemplateId } : null;
    }
    case "import-alerts": {
      const integrationId = sp.get("integrationId");
      if (integrationId) {
        return { dialog, integrationId };
      }
      return { dialog };
    }

    case "edit-dashboard": {
      const dashboardId = sp.get("dashboardId");
      return dashboardId ? { dialog, dashboardId } : null;
    }

    case "edit-alert": {
      const alertId = sp.get("alertId");
      return alertId ? { dialog, alertId } : null;
    }

    case "edit-integration": {
      const integrationId = sp.get("integrationId");
      return integrationId ? { dialog, integrationId } : null;
    }

    case "edit-alert-schema": {
      const alertSchemaId = sp.get("alertSchemaId");
      return alertSchemaId ? { dialog, alertSchemaId } : null;
    }

    case "new-alert-event": {
      const alertId = sp.get("alertId");
      return alertId ? { dialog, alertId } : null;
    }

    case "setup-integration": {
      const integrationId = sp.get("integrationId");
      return integrationId ? { dialog, integrationId } : null;
    }

    case "new-business-service": {
      const dashboardId = sp.get("dashboardId") ?? getDashboardIdFromPath(pathname);
      return dashboardId ? { dialog, dashboardId } : null;
    }
    case "edit-service": {
      const dashboardId = sp.get("dashboardId") ?? getDashboardIdFromPath(pathname);
      const serviceId = sp.get("serviceId") ?? getServiceIdFromPath(pathname);
      return dashboardId && serviceId ? { dialog, dashboardId, serviceId } : null;
    }
    case "manage-dependencies": {
      const serviceMapId = sp.get("serviceMapId");
      const serviceId = sp.get("serviceId");
      return serviceMapId && serviceId ? { dialog, serviceMapId, serviceId } : null;
    }
    case "manage-kpi-alert-mappings": {
      const serviceMapId = sp.get("serviceMapId");
      const serviceId = sp.get("serviceId");
      return serviceMapId && serviceId ? { dialog, serviceMapId, serviceId } : null;
    }

    case "confirm-delete": {
      const entity = sp.get("entity");
      const ids = sp.get("ids");
      const serviceMapId = sp.get("serviceMapId") ?? undefined;

      const entitySchema = z.enum([
        "dashboard",
        "alert",
        "alert-schema",
        "integration",
        "service",
        "kpi",
        "service-template",
        "user",
      ]);
      const parsed = entitySchema.safeParse(entity);

      if (!parsed.success || !ids) {return null;}
      return { dialog, entity: parsed.data, ids, serviceMapId };
    }

    default:
      return null;
  }
};

const toSearchParams = (d: DialogState): Record<string, string> => {
  switch (d.dialog) {
    case "edit-dashboard":
      return { dialog: d.dialog, dashboardId: d.dashboardId };
    case "edit-alert":
      return { dialog: d.dialog, alertId: d.alertId };
    case "edit-integration":
      return { dialog: d.dialog, integrationId: d.integrationId };
    case "edit-alert-schema":
      return { dialog: d.dialog, alertSchemaId: d.alertSchemaId };
    case "new-alert-event":
      return { dialog: d.dialog, alertId: d.alertId };
    case "setup-integration":
      return { dialog: d.dialog, integrationId: d.integrationId };
    case "import-alerts":
      if (d.integrationId) {
        return { dialog: d.dialog, integrationId: d.integrationId };
      }
      return { dialog: d.dialog };
    case "new-business-service":
      return { dialog: d.dialog };
    case "edit-service":
      return {
        dialog: d.dialog,
        serviceId: d.serviceId,
      };
    case "edit-kpi":
      return { dialog: d.dialog, kpiId: d.kpiId };
    case "edit-service-template":
      return {
        dialog: d.dialog,
        serviceTemplateId: d.serviceTemplateId,
      };
    case "edit-user":
      return { dialog: d.dialog, userId: d.userId };
    case "confirm-delete":
      if (d.serviceMapId) {
        return {
          dialog: d.dialog,
          entity: d.entity,
          ids: d.ids,
          serviceMapId: d.serviceMapId,
        };
      }
      return { dialog: d.dialog, entity: d.entity, ids: d.ids };
    case "manage-dependencies":
      return { dialog: d.dialog, serviceMapId: d.serviceMapId, serviceId: d.serviceId };
    case "manage-kpi-alert-mappings":
      return { dialog: d.dialog, serviceMapId: d.serviceMapId, serviceId: d.serviceId };
    default:
      return { dialog: d.dialog };
  }
};

const DIALOG_KEYS = [
  "dialog",
  "dashboardId",
  "alertId",
  "integrationId",
  "alertSchemaId",
  "entity",
  "ids",
  "serviceMapId",
  "serviceId",
  "kpiId",
  "serviceTemplateId",
  "userId",
] as const;

const applyDialogParams = (
  prev: URLSearchParams,
  nextDialog: DialogState | null,
): URLSearchParams => {
  const next = new URLSearchParams(prev);

  for (const k of DIALOG_KEYS) {next.delete(k);}

  if (nextDialog) {
    const entries = toSearchParams(nextDialog);
    for (const [k, v] of Object.entries(entries)) {next.set(k, v);}
  }

  return next;
};

export const DialogProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();

  const active = useMemo(
    () => parseDialog(searchParams, location.pathname),
    [location.pathname, searchParams],
  );

  const [initialKey] = useState(() => location.key);
  const [initialHadDialog] = useState(() => active !== null);

  const close = useCallback(() => {
    if (!active) {return;}

    if (initialHadDialog && location.key === initialKey) {
      setSearchParams((prev) => applyDialogParams(prev, null), { replace: true });
      return;
    }

    navigate(-1);
  }, [
    active,
    initialHadDialog,
    initialKey,
    location.key,
    navigate,
    setSearchParams,
  ]);

  const show: ShowFunc = useCallback(
    (nextDialog, options) => {
      const replace =
        options?.transient ?? (active?.dialog === nextDialog.dialog);

      setSearchParams((prev) => applyDialogParams(prev, nextDialog), { replace });
    },
    [active?.dialog, setSearchParams],
  );

  const previous = useCallback(() => navigate(-1), [navigate]);
  const next = useCallback(() => navigate(1), [navigate]);
  const hasPrevious =
    Boolean(active) && !(initialHadDialog && location.key === initialKey);
  const hasNext = false;

  const closeHandler: CloseDialogHandler = useCallback(
    (toast) => {
      const schema = z.object({
        message: z.string(),
        severity: z.enum(["success", "info", "warning", "error"]),
      });

      const parsed = schema.safeParse(toast);
      if (parsed.success) {
        showToast(parsed.data.message, parsed.data.severity);
      }

      close();
    },
    [close, showToast],
  );

  return (
    <DialogContext.Provider
      value={{ active, show, close, hasPrevious, hasNext, previous, next }}
    >
      {children}

      {active?.dialog === "import-alerts" && (
        <ImportAlertsDialog
          onDialogClosed={closeHandler}
          integrationId={active.integrationId}
        />
      )}
      {active?.dialog === "new-dashboard" && (
        <NewDashboardDialog onDialogClosed={closeHandler} />
      )}
      {active?.dialog === "edit-dashboard" && (
        <EditDashboardDialog
          onDialogClosed={closeHandler}
          dashboardId={active.dashboardId}
        />
      )}
      {active?.dialog === "setup-integration" && (
        <SetupIntegrationDialog
          onDialogClosed={closeHandler}
          integrationId={active.integrationId}
        />
      )}
      {active?.dialog === "new-integration" && (
        <NewIntegrationDialog onDialogClosed={closeHandler} />
      )}
      {active?.dialog === "edit-integration" && (
        <EditIntegrationDialog
          onDialogClosed={closeHandler}
          integrationId={active.integrationId}
        />
      )}
      {active?.dialog === "new-alert-event" && (
        <NewAlertEventDialog
          alertId={active.alertId}
          onDialogClosed={closeHandler}
        />
      )}
      {active?.dialog === "new-business-service" && (
        <NewServiceDialog
          onDialogClosed={closeHandler}
          dashboardId={active.dashboardId}
        />
      )}
      {active?.dialog === "edit-service" && (
        <EditServiceDialog
          onDialogClosed={closeHandler}
          dashboardId={active.dashboardId}
          serviceId={active.serviceId}
        />
      )}
      {active?.dialog === "new-alert" && (
        <NewAlertDialog onDialogClosed={closeHandler} />
      )}
      {active?.dialog === "edit-alert" && (
        <EditAlertDialog
          onDialogClosed={closeHandler}
          alertId={active.alertId}
        />
      )}
      {active?.dialog === "new-alert-schema" && (
        <NewAlertSchemaDialog onDialogClosed={closeHandler} />
      )}
      {active?.dialog === "new-kpi" && (
        <NewKpiDialog onDialogClosed={closeHandler} />
      )}
      {active?.dialog === "new-service-template" && (
        <NewServiceTemplateDialog onDialogClosed={closeHandler} />
      )}
      {active?.dialog === "edit-kpi" && (
        <EditKpiDialog onDialogClosed={closeHandler} kpiId={active.kpiId} />
      )}
      {active?.dialog === "edit-service-template" && (
        <EditServiceTemplateDialog
          onDialogClosed={closeHandler}
          serviceTemplateId={active.serviceTemplateId}
        />
      )}
      {active?.dialog === "edit-alert-schema" && (
        <EditAlertSchemaDialog
          onDialogClosed={closeHandler}
          alertSchemaId={active.alertSchemaId}
        />
      )}

      {active?.dialog === "confirm-delete" && (
        <ConfirmDeleteDialog
          onDialogClosed={closeHandler}
          entity={active.entity}
          ids={active.ids}
          serviceMapId={active.serviceMapId}
        />
      )}
      {active?.dialog === "manage-dependencies" && (
        <ManageDependenciesDialog
          onDialogClosed={closeHandler}
          serviceMapId={active.serviceMapId}
          serviceId={active.serviceId}
        />
      )}
      {active?.dialog === "manage-kpi-alert-mappings" && (
        <ManageKpiAlertMappingsDialog
          onDialogClosed={closeHandler}
          serviceMapId={active.serviceMapId}
          serviceId={active.serviceId}
        />
      )}
      {active?.dialog === "invite-user" && (
        <InviteUserDialog onDialogClosed={closeHandler} />
      )}
      {active?.dialog === "edit-user" && (
        <EditUserDialog onDialogClosed={closeHandler} userId={active.userId} />
      )}

      {ToastComponent}
    </DialogContext.Provider>
  );
};

export default DialogContext;
